/**
 * Emoji shortcodes: `:smile:` → 😀.
 *
 * A tiny, dependency-free table that the composer and message renderer use
 * to expand GitHub-style shortcodes into unicode emoji. Unknown shortcodes
 * pass through unchanged so `:not-a-real-thing:` renders literally rather
 * than throwing or rendering an empty box.
 *
 * Lookup is case-insensitive on the key (` :THUMBSUP: ` works the same as
 * ` :thumbsup: `) but the output preserves the actual codepoint we mapped.
 *
 * `parseShortcodes` returns the resolved text + the span of each replaced
 * shortcode so downstream scanners (Markdown, URL extractor) can skip them.
 */

export const EMOJI_MAP: Record<string, string> = {
  ":smile:": "😀",
  ":grinning:": "😀",
  ":laughing:": "😆",
  ":joy:": "😂",
  ":rofl:": "🤣",
  ":cry:": "😢",
  ":sob:": "😭",
  ":heart:": "❤️",
  ":hearts:": "❤️",
  ":thumbsup:": "👍",
  ":+1:": "👍",
  ":thumbsdown:": "👎",
  ":-1:": "👎",
  ":fire:": "🔥",
  ":tada:": "🎉",
  ":party:": "🎉",
  ":wave:": "👋",
  ":rocket:": "🚀",
  ":eyes:": "👀",
  ":100:": "💯",
  ":star:": "⭐",
  ":check:": "✔️",
  ":x:": "❌",
};

// Build a case-insensitive lookup without rebuilding the public map. Stored
// alongside EMOJI_MAP so additions to the map stay a single place to edit.
const EMOJI_LOOKUP: Map<string, string> = new Map(
  Object.entries(EMOJI_MAP).map(([k, v]) => [k.toLowerCase(), v]),
);

// Match `:name:` where `name` is a non-empty run of word characters
// (letters, digits, underscore, hyphen, plus). Matches the shortcode shape
// the spec calls out — `:+1:` and `:-1:` rely on the plus/minus, the rest
// fall under \w.
const SHORTCODE_RE = /:([+\-]?\w+):/g;

export function shortcodeToEmoji(text: string): string {
  if (!text || text.indexOf(":") === -1) return text;
  return text.replace(SHORTCODE_RE, (match, name: string) => {
    const emoji = EMOJI_LOOKUP.get(`:${name.toLowerCase()}:`);
    return emoji ?? match;
  });
}

export interface ShortcodePosition {
  start: number;
  end: number;
  emoji: string;
}

/**
 * Walk the input once and return the resolved text plus the byte offsets of
 * every shortcode we replaced (pointing at the *replaced* emoji, not the
 * original `:foo:`). Callers use `positions` to mask the original when
 * scanning for URLs / markdown so we don't pick up tokens like
 * `https://example.com/:smile:` inside a code block.
 */
export function parseShortcodes(content: string): {
  text: string;
  positions: ShortcodePosition[];
} {
  const positions: ShortcodePosition[] = [];
  if (!content || content.indexOf(":") === -1) {
    return { text: content, positions };
  }
  let out = "";
  let last = 0;
  // `matchAll` instead of `replace` so we can compute the absolute offsets of
  // the replacement before splicing the string — `replace` only gives us the
  // matched substring, not the position in the original.
  for (const match of content.matchAll(SHORTCODE_RE)) {
    const full = match[0];
    const name = match[1];
    const start = match.index ?? 0;
    const end = start + full.length;
    const emoji = EMOJI_LOOKUP.get(`:${name.toLowerCase()}:`);
    if (!emoji) continue;
    out += content.slice(last, start) + emoji;
    last = end;
    positions.push({ start: out.length - emoji.length, end: out.length, emoji });
  }
  out += content.slice(last);
  return { text: out, positions };
}